import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatMoney, orderAmount, t } from '@aquabox/shared';
import { Icon } from '../components/Icon';
import { ChannelPill } from '../components/Pills';
import { useToast } from '../components/Toast';
import { useOrderMutations, useUnmappedCount, useVehicles } from '../api/queries';
import { ApiError } from '../api/client';
import { itemsSummary, relativeTime } from '../lib/labels';
import type { Alert, Dashboard, Order } from '../api/types';
import { KpiCards, RegionMissingBanner } from './overview/KpiCards';
import { LiveOrdersCard } from './overview/LiveOrdersCard';
import { PanelTodayCard } from './overview/PanelTodayCard';
import { ChannelDistributionCard } from './overview/ChannelDistributionCard';
import { ClosingStatusCard } from './overview/ClosingStatusCard';
import { RecentOrdersCard } from './overview/RecentOrdersCard';
import { DayCloseCard } from './overview/DayCloseCard';
import { TodaySummary } from './overview/TodaySummary';

const ATTENTION_PREVIEW = 5;
const LEVEL_RANK: Record<Alert['level'], number> = { CRITICAL: 0, WARNING: 1, INFO: 2 };

const LEVEL_COLOR: Record<Alert['level'], string> = {
  CRITICAL: 'var(--danger)',
  WARNING: 'var(--warning)',
  INFO: 'var(--info)',
};

const LEVEL_ICON: Record<Alert['level'], 'alert-circle' | 'alert-triangle' | 'info'> = {
  CRITICAL: 'alert-circle',
  WARNING: 'alert-triangle',
  INFO: 'info',
};

/**
 * Genel Bakış (§3.3, kullanıcı kararı 30.09.2026):
 *
 * Bugünün gerçeği tek ekranda: KPI + kaynak kırılımı + anlık siparişler (panel dahil) +
 * panelde teslim kartı + kanal dağılımı + kapanış durumu + dikkat + son siparişler.
 *
 * Defter mantığı burada YOK — bu ekran yalnız sunumdur; sipariş durumu ya da sayımı
 * değişmez (`orders/trips/stock` servislerine dokunulmaz).
 */
export function Overview({ data }: { data: Dashboard }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { autoAssign, assign } = useOrderMutations();
  const vehicles = useVehicles();
  const unmapped = useUnmappedCount();
  // Bölge eşleşmeyen sipariş için şoförün SEÇTİĞİ araç (sipariş id → araç id). Önceden burada
  // sessizce alfabetik ilk araca atanıyordu (Faz 1 denetimi U6) — prototip §5.1 madde 5'in kendisi
  // bile bunu "üretim için yetersiz" diye işaretlemişti. Şimdi patron hangi araca gideceğini görür.
  const [manualVehicle, setManualVehicle] = useState<Record<string, string>>({});

  async function handleAssign(order: Order): Promise<void> {
    try {
      if (order.region) {
        const updated = await autoAssign.mutateAsync(order.id);
        toast(t('panel.assignedToast', { plate: updated.vehicle?.plate ?? '' }));
        return;
      }
      const vehicleId = manualVehicle[order.id];
      if (!vehicleId) return;
      const updated = await assign.mutateAsync({ id: order.id, vehicleId });
      toast(t('panel.assignedToast', { plate: updated.vehicle?.plate ?? '' }));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('error.internal'), 'danger');
    }
  }

  const { today, alerts, dormant, unassignedOrders, recentOrders, liveOrders, activeRegionCount } =
    data;

  return (
    <div className="page">
      {/*
        Sıralama, önem sırasıyla (30.09.2026 kullanıcı kararı — "hiçbir şey eksiltmeyelim"):
        1) KPI  2) dikkat gerektirenler  3) canlı siparişler (+ elle atama)
        4) panel bugün  5) kanal dağılımı  6) kapanış  7) son siparişler.
      */}
      <RegionMissingBanner activeRegionCount={activeRegionCount} />
      {/* "Günü gördüm" kartı UX'ten gizli (31.10); `VITE_DAYCLOSE=1` ile açılır. */}
      {import.meta.env.VITE_DAYCLOSE === '1' ? <DayCloseCard /> : null}
      <TodaySummary today={today} />
      <KpiCards today={today} />

      <AttentionNeededSection
        alerts={alerts}
        dormant={dormant ?? { items: [], total: 0 }}
        unmappedCount={unmapped.data?.count ?? 0}
        onNavigate={(target) =>
          void navigate(target === 'customers' ? '/musteriler' : '/siparisler')
        }
      />

      <LiveOrdersCard orders={liveOrders} />

      <ManualAssignSection
        unassigned={unassignedOrders}
        manualVehicle={manualVehicle}
        setManualVehicle={setManualVehicle}
        onAssign={handleAssign}
        vehicles={vehicles.data?.items ?? []}
        assignPending={assign.isPending}
        autoAssignPending={autoAssign.isPending}
      />

      <PanelTodayCard panel={today.panel} />

      <section
        className="section-grid"
        style={{ '--section-cols': '3fr 2fr' } as React.CSSProperties}
      >
        <ChannelDistributionCard byChannel={today.byChannel} bySource={today.bySource} />
        <ClosingStatusCard />
      </section>

      <RecentOrdersCard orders={recentOrders} />
    </div>
  );
}

/**
 * "Elle atama bekleyenler" kartı — sıfır olunca yeşil "yok" rozeti gösteren eski kart
 * olduğu gibi duruyor. Yalnız BİZİM siparişler buraya girer (panel siparişleri için
 * patron bir şey yapamaz; sunucu tarafı zaten filtreliyor).
 */
function ManualAssignSection({
  unassigned,
  manualVehicle,
  setManualVehicle,
  onAssign,
  vehicles,
  assignPending,
  autoAssignPending,
}: {
  unassigned: Order[];
  manualVehicle: Record<string, string>;
  setManualVehicle: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onAssign: (order: Order) => Promise<void>;
  vehicles: { id: string; plate: string }[];
  assignPending: boolean;
  autoAssignPending: boolean;
}) {
  return (
    <section className="card card-pad">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--info)' }} />
        <h2 className="section-title">{t('panel.manualAssignTitle')}</h2>
        {unassigned.length > 0 && (
          <span className="badge-amber">
            {t('panel.awaitingManualAssign', { n: unassigned.length })}
          </span>
        )}
      </div>
      <p className="muted-line" style={{ marginBottom: 14 }}>
        {t('panel.liveOrdersHint')}
      </p>

      {unassigned.length === 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '26px 24px',
          }}
        >
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'rgba(30,158,106,.12)',
              color: 'var(--success)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="check" strokeWidth={2.2} />
          </span>
          <span style={{ fontWeight: 500, fontSize: 14.5, color: 'var(--text-secondary)' }}>
            {t('panel.noManualAssign')}
          </span>
        </div>
      ) : (
        <ul style={{ listStyle: 'none' }}>
          {unassigned.map((order) => (
            <li
              key={order.id}
              className="rowgrid-stack"
              style={{
                display: 'grid',
                gridTemplateColumns: '44px 150px 150px 1fr 140px 88px 150px 200px',
                alignItems: 'center',
                gap: 12,
                padding: '14px 6px',
                borderTop: '1px solid var(--border)',
                borderLeft: `3px solid ${order.region ? 'var(--success)' : 'var(--warning)'}`,
                paddingLeft: 15,
                background: order.region ? 'var(--bg-success-soft)' : undefined,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13 }}>{timeLabel(order.receivedAt)}</span>
              <ChannelPill channel={order.channel} source={order.source} />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 14.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {order.customerName}
              </span>
              <span
                style={{
                  fontSize: 13.5,
                  color: 'var(--text-secondary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {order.address}
              </span>
              <span style={{ fontWeight: 500, fontSize: 13.5 }}>{itemsSummary(order.items)}</span>
              <span style={{ fontWeight: 600, fontSize: 14, textAlign: 'right' }}>
                {formatMoney(orderAmount(order))}
              </span>
              <span style={{ fontSize: 12.5 }}>
                <span style={{ color: 'var(--warning)', fontWeight: 500 }}>
                  {relativeTime(order.receivedAt)}
                </span>
                <br />
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 11.5,
                    color: order.region ? 'var(--success)' : 'var(--warning)',
                  }}
                >
                  {order.region ? order.region.name : t('order.regionUnmatched')}
                </span>
              </span>
              {order.region ? (
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 13, padding: '9px 14px', justifyContent: 'center' }}
                  disabled={autoAssignPending}
                  onClick={() => {
                    void onAssign(order);
                  }}
                >
                  {t('panel.autoAssign')}
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <select
                    className="input"
                    style={{ height: 34, fontSize: 12, padding: '0 6px' }}
                    value={manualVehicle[order.id] ?? ''}
                    aria-label={t('panel.pickVehicle')}
                    onChange={(e) =>
                      setManualVehicle((m) => ({ ...m, [order.id]: e.target.value }))
                    }
                  >
                    <option value="">{t('panel.pickVehicle')}</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.plate}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 12.5, padding: '6px 10px', justifyContent: 'center' }}
                    disabled={!manualVehicle[order.id] || assignPending}
                    onClick={() => {
                      void onAssign(order);
                    }}
                  >
                    {t('panel.assign')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AttentionNeededSection({
  alerts,
  dormant,
  unmappedCount,
  onNavigate,
}: {
  alerts: Alert[];
  dormant: { items: Alert[]; total: number };
  unmappedCount: number;
  onNavigate: (target: 'customers' | 'orders') => void;
}) {
  // Uyarılar önem sırasıyla; ilk beşi görünür, gerisi tek tıkla açılır. Liste uzun olunca
  // (ör. onlarca "X gündür sipariş vermedi") ekranın geri kalanını aşağı itmesin.
  const [expanded, setExpanded] = useState(false);
  const sorted = [...alerts].sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
  const visible = expanded ? sorted : sorted.slice(0, ATTENTION_PREVIEW);
  return (
    <section className="card card-pad">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <h2 className="section-title">{t('panel.attentionNeeded')}</h2>
        {alerts.length > 0 && <span className="badge-amber">{alerts.length}</span>}
      </div>
      {unmappedCount > 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '13px 14px',
            borderLeft: '3px solid var(--warning)',
            background: 'var(--bg-warning-soft)',
            borderTop: '1px solid var(--border)',
            marginBottom: alerts.length === 0 ? 0 : undefined,
          }}
        >
          <span style={{ color: 'var(--warning)', display: 'inline-flex' }}>
            <Icon name="alert-triangle" />
          </span>
          <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45 }}>
            {t('panelUi.unmappedAlert', { n: unmappedCount })}
          </span>
          <Link
            to="/entegrasyonlar?tab=urun"
            style={{
              color: 'var(--primary)',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {t('panelUi.unmappedAlertLink')}
            <Icon name="arrow-right" size={15} />
          </Link>
        </div>
      ) : null}
      {alerts.length === 0 && dormant.total === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'rgba(30,158,106,.12)',
              color: 'var(--success)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="check" strokeWidth={2.2} />
          </span>
          <span style={{ fontWeight: 600, fontSize: 16 }}>{t('panel.allGood')}</span>
        </div>
      ) : (
        <ul style={{ listStyle: 'none' }}>
          {visible.map((alert) => (
            <li
              key={alert.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '13px 14px',
                borderLeft: `3px solid ${LEVEL_COLOR[alert.level]}`,
                background: alert.level === 'CRITICAL' ? 'var(--bg-danger-soft)' : undefined,
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ color: LEVEL_COLOR[alert.level], display: 'inline-flex' }}>
                <Icon name={LEVEL_ICON[alert.level]} />
              </span>
              <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45 }}>{alert.message}</span>
              <button
                className="btn"
                style={{ color: 'var(--primary)', fontSize: 13, padding: 0 }}
                onClick={() => onNavigate(alert.target === 'customers' ? 'customers' : 'orders')}
              >
                {alert.target === 'customers' ? t('panel.goToCustomers') : t('panel.goToOrders')}
                <Icon name="arrow-right" size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {alerts.length > ATTENTION_PREVIEW ? (
        <button
          type="button"
          className="btn"
          style={{ marginTop: 10, color: 'var(--primary)', fontSize: 13, padding: 0 }}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? t('panelUi.attentionShowLess')
            : t('panelUi.attentionShowAll', { n: alerts.length })}
        </button>
      ) : null}
      {dormant.total > 0 ? (
        // Ayrı grup: gürültülü "uzun süredir almayanlar" ana listeyi gömmesin (ana liste önce).
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <h3 className="section-title" style={{ fontSize: 15 }}>
              {t('panel.dormantGroup')}
            </h3>
            <span className="badge-amber">{dormant.total}</span>
          </div>
          <ul style={{ listStyle: 'none' }}>
            {dormant.items.map((alert) => (
              <li
                key={alert.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45 }}>{alert.message}</span>
                <button
                  className="btn"
                  style={{ color: 'var(--primary)', fontSize: 13, padding: 0 }}
                  onClick={() => onNavigate('customers')}
                >
                  {t('panel.goToCustomers')}
                  <Icon name="arrow-right" size={15} />
                </button>
              </li>
            ))}
          </ul>
          {dormant.total > dormant.items.length ? (
            <p style={{ marginTop: 8, fontSize: 13, lineHeight: 1.45 }}>
              {t('panel.dormantMore', { n: dormant.total - dormant.items.length })}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}
